import React, { useState } from 'react';
import { Box, Container, Typography, Button, Card, CardContent } from '@mui/material';
import { useNavigate } from 'react-router-dom';
// import { FilterListIcon } from '@mui/icons-material';
import FilterListIcon from '@mui/icons-material/FilterList';

import PageLayout from '../../components/layout/PageLayout';
// import LeadTable from '../../components/Leads/'
import {
  LeadTable,
  LeadFilters,
  LeadActionsMenu,
  StatusDialog,
  AssignDialog,
  ShareDialog,
  AttachmentsDialog,
  AssignedUsersDialog,
  useLeads,
  useLeadActions
} from '../../components/Leads'; // This should point to your leads components folder
import { useAuth } from '../../hooks/useAuth';
import { toast } from 'react-toastify';
import Can from '../../components/permissions/Can';
import IssueDialog from '../../components/Leads/dialogs/IssueDialog';
import CreateCustomerDialog from '../../components/Leads/dialogs/CreateCustomerDialog';
import CustomerCredentialsDialog from '../../components/Leads/dialogs/CustomerCredentialsDialog';
import ImportLeadDialog from '../../components/Leads/dialogs/ImportLeadDialog';


const LeadsList = () => {
  const navigate = useNavigate();

  const { hasPermission } = useAuth();

  // Add permission-based conditions
  const canViewListLead = hasPermission('lead-list');
  const canViewLeads = hasPermission('lead-view');
  const canCreateLeads = hasPermission('lead-create');
  const canEditLeads = hasPermission('lead-edit');
  const canChangeLeadStatus = hasPermission('lead-status');
  const canAssignLeads = hasPermission('lead-assign-user');
  const canDeleteLeads = hasPermission('lead-delete');
  const canCreateShareableLink = hasPermission('shareable-link-create');
  const canViewAssignedUsers = hasPermission('lead-assigned-users-list');
  // create customer
  const canCreateCustomer = hasPermission('customer-create');

// import data 
// const [importDialogOpen, setImportDialogOpen] = useState(false);
const [importDialogOpen, setImportDialogOpen] = useState(false);

  const handleOpenImport = () => {
    setImportDialogOpen(true);
  };

  const handleCloseImport = (success) => {
    setImportDialogOpen(false);
    if (success) {
      // Optionally refresh your leads table here
      // e.g. fetchLeads();
    }
  };

  // Use custom hooks
  const leadsData = useLeads(canViewListLead);
  const leadActions = useLeadActions(leadsData, {
    canChangeLeadStatus,
    canAssignLeads,
    canDeleteLeads,
    canCreateShareableLink,
    canViewListLead
  });

  const [showFilters, setShowFilters] = useState(false);
  const [selectedLead, setSelectedLead] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);

  const handleOpenMenu = (e, lead) => {
    if (!canViewListLead) return;
    setAnchorEl(e.currentTarget);
    setSelectedLead(lead);
    leadActions.setSelectedLead(lead);
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
    setSelectedLead(null);
    leadActions.setSelectedLead(null);
  };

  const handleCreateLead = () => {
    if (!canCreateLeads) {
      toast.error("You do not have permission to create lead")
      return;
    }
    navigate('/leads/create')
  };

  // If user doesn't have view permission, show access denied
  if (!canViewListLead) {
    return (
      <PageLayout>
        <Container maxWidth="xl" sx={{ py: 4, textAlign: 'center' }}>
          <Typography variant="h5" color="error">
            Access Denied - You don't have permission to view leads
          </Typography>
        </Container>
      </PageLayout>
    );
  }
  return (
    <PageLayout>
      <Container maxWidth="xl" sx={{ py: 4 }}>
          {/* Header */}
          <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h4" sx={{ color: 'blue.600', fontWeight: 600 }}>
              Leads
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
              <LeadTable.SearchBar
                searchValue={leadsData.searchValue}
                onSearchChange={leadsData.handleSearchChange}
                onClearSearch={leadsData.handleClearSearch}
              />
              <Button
                variant="outlined"
                startIcon={<FilterListIcon />}
                onClick={() => setShowFilters(!showFilters)}
                sx={{
                  position: 'relative',
                  ...(leadsData.getActiveFiltersCount() > 0 && {
                    borderColor: 'primary.main',
                    backgroundColor: 'primary.light',
                  })
                }}
              >
                Filters
                {leadsData.getActiveFiltersCount() > 0 && (
                  <Box
                    sx={{
                      position: 'absolute',
                      top: -8,
                      right: -8,
                      backgroundColor: 'primary.main',
                      color: 'white',
                      borderRadius: '50%',
                      width: 20,
                      height: 20,
                      fontSize: '0.75rem',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    {leadsData.getActiveFiltersCount()}
                  </Box>
                )}
              </Button>
              <Can permission='lead-create' >
                <Button
                  variant="contained"
                  onClick={handleCreateLead}
                  sx={{ bgcolor: 'blue.600', '&:hover': { bgcolor: 'blue.700' } }}
                >
                  Create New Lead
                </Button>
              </Can>
              <Can permission='lead-export' >
                <Button
                  variant="contained"
                  onClick={handleOpenImport}
                  sx={{ bgcolor: 'blue.600', '&:hover': { bgcolor: 'blue.700' } }}
                >
                  Import Data
                </Button>
              </Can>
            </Box>
          </Box>

          {/* Filters Section */}
          <LeadFilters
            showFilters={showFilters}
            filters={leadsData.filters}
            sort={leadsData.sort}
            noActivity={leadsData.noActivity}
            statusOptions={leadsData.statusOptions}
            usersList={leadsData.usersList}
            onFilterChange={leadsData.handleFilterChange}
            onSortChange={leadsData.handleSortChange}
            onNoActivityChange={leadsData.handleNoActivityChange}
            onClearFilters={leadsData.clearFilters}
          />

          {/* Table */}
          <Card>
            <CardContent sx={{ p: 0 }}>
              <LeadTable
                leads={leadsData.leads}
                loading={leadsData.tableLoading}
                pagination={leadsData.pagination}
                sort={leadsData.sort}
                onOpenMenu={handleOpenMenu}
                processingId={leadsData.processingId}
                onPageChange={leadsData.handlePageChange}
                onRowsPerPageChange={leadsData.handleRowsPerPageChange}
                onSortChange={leadsData.handleSortChange}
                onDownloadFile={leadActions.handleDownloadFile}
                canViewListLead={canViewListLead}
              />
            </CardContent>
          </Card>
      </Container>

      {/* Action Menu */}
      <LeadActionsMenu
        anchorEl={anchorEl}
        selectedLead={selectedLead} // This should be the lead from state
        onClose={handleCloseMenu}
        onViewLead={leadActions.handleViewLead}
        onEditLead={leadActions.handleEditLead}
        onCreateDba={leadActions.handleCreateDba}
        onEditDba={leadActions.handleEditDba}
        onOpenStatusDialog={() => leadActions.handleOpenStatusDialog(selectedLead)}
        onOpenAssignDialog={() => leadActions.handleOpenAssignDialog(selectedLead)}
        onOpenShareDialog={() => leadActions.handleOpenShareDialog(selectedLead)}
        onOpenAttachmentsDialog={() => leadActions.handleOpenAttachmentsDialog(selectedLead)}
        onOpenAssignedUsersDialog={() => leadActions.handleOpenAssignedUsersDialog(selectedLead)}
        onOpenIssueDialog={() => leadActions.handleOpenIssueDialog(selectedLead)}
        onOpenCreateCustomerDialog={() => leadActions.handleOpenCreateCustomerDialog(selectedLead)} // ✅ Make sure this passes selectedLead
        onDeleteLead={leadActions.handleDeleteLead}
        permissions={{
          canViewLeads,
          canEditLeads,
          canChangeLeadStatus,
          canAssignLeads,
          canDeleteLeads,
          canCreateShareableLink,
          canViewAssignedUsers,
          canCreateCustomer
        }}
      />

      {/* Dialogs */}
      {canChangeLeadStatus && (
        <StatusDialog
          open={leadActions.dialogs.status}
          selectedLead={selectedLead}
          newStatus={leadActions.statusData.newStatus}
          conditionalData={leadActions.statusData.conditionalData}
          statusOptions={leadsData.statusOptions}
          frontSellerOptions={leadsData.frontSellerOptions}
          processingId={leadsData.processingId}
          onClose={leadActions.handleCloseStatusDialog}
          onStatusChange={leadActions.handleStatusChange}
          onNewStatusChange={leadActions.setNewStatus}
          onConditionalChange={leadActions.handleConditionalChange}
        />
      )}

      {canAssignLeads && (

        <AssignDialog
          open={leadActions.dialogs.assign}
          selectedLead={selectedLead}
          selectedUsers={leadActions.assignData.selectedUsers}
          usersList={leadsData.usersList}
          processingId={leadsData.processingId}
          onClose={leadActions.handleCloseAssignDialog}
          onUsersChange={leadActions.setSelectedUsers}
          onAssign={leadActions.handleAssign}
        />
      )}

      {canCreateShareableLink && (

        <ShareDialog
          open={leadActions.dialogs.share}
          shareData={leadActions.shareData}
          shareLoading={leadActions.shareLoading}
          onClose={leadActions.handleCloseShareDialog}
          onCopy={leadActions.copyToClipboard}
        />

      )}


      <AttachmentsDialog
        open={leadActions.dialogs.attachments}
        attachments={leadActions.attachments}
        attachmentsLoading={leadActions.attachmentsLoading}
        downloadingIndex={leadActions.downloadingIndex}
        onClose={leadActions.handleCloseAttachmentsDialog}
        onDownload={leadActions.handleDownloadFile}
      />

      {canViewAssignedUsers && (
        <AssignedUsersDialog
          open={leadActions.dialogs.assignedUsers}
          assignedUsers={leadActions.assignedUsers}
          assignedUsersLoading={leadActions.assignedUsersLoading}
          onClose={leadActions.handleCloseAssignedUsersDialog}
        />
      )}


      {/* Add the Issue Dialog */}
      <IssueDialog
        open={leadActions.dialogs.issue}
        selectedLead={selectedLead}
        processing={leadsData.processingId === selectedLead?.id}
        onClose={leadActions.handleCloseIssueDialog}
        onSubmit={leadActions.handleCreateIssue}
      />

      {/* Add this with other dialogs */}
      {canCreateCustomer && (
        <CreateCustomerDialog
          open={leadActions.dialogs.createCustomer}
          selectedLead={selectedLead}
          processing={leadsData.processingId === selectedLead?.id}
          onClose={leadActions.handleCloseCreateCustomerDialog}
          onSubmit={leadActions.handleCreateCustomer}
        />
      )}
      <CustomerCredentialsDialog
        open={leadActions.showCredentialsDialog}
        customerData={leadActions.createdCustomer}
        onClose={leadActions.handleCloseCredentialsDialog}
      />

      <ImportLeadDialog
        open={importDialogOpen}
        onClose={handleCloseImport}
      />

    </PageLayout>
  );
};

export default LeadsList;